import React, { useState, useCallback, useEffect } from 'react';
import { InputComponent, DropdownComponent} from '../../core/Forms';
import { Button, SearchInput } from '../../common';
import bankAccount from '../../../constants/bankAccount.json';
import { BankData } from '../../../types';
import { clearFieldError } from '../../../utils/handleErrors';
import { ValidationError, validateBankForm } from '../../../modules/settings/settings';
import { useBankAccountSearch } from '../../../hooks/bankAccount/useBankSearch';

interface BankAccountFormProps {
  initialData: BankData;
  edit?: boolean;
  onSubmit: (data: BankData) => Promise<void>;
  bankNameInput: string;
}

interface Bank {
  value: string;
  label: string;
}
export const BankAccountForm: React.FC<BankAccountFormProps> = ({ initialData, edit = false, onSubmit }) => {
  const [formData, setFormData] = useState(initialData);
  const [isLoading, setIsLoading] = useState(false);
  const [validationResults, setValidationResults] = useState<ValidationError>({
    bankName: '',
    accountNumber: '',
    accountName: '',
  });

  const [, setBankAccount] = useState<Bank[]>([]);

  const [selectedBank, setSelectedBank] = useState<Bank | null>(null);
  

  const { bankAccount, isSearching, handleSearch } = useBankAccountSearch();

  
const handleInputChange = useCallback(
  (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    if (name === 'accountNumber') {
      const numericValue = value.replace(/\D/g, '');
      const restrictedValue = numericValue.slice(0, 10);

      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: restrictedValue,
      }));
    } else {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: value,
      }));
    }

    clearFieldError(name, validationResults, setValidationResults);
  },
  [formData, validationResults],
);
  const buttonText = edit ? 'Save Changes' : 'Add Bank Account';

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!validateBankForm(formData, setValidationResults)) {
        setIsLoading(true);
        try {
          await onSubmit({
            ...formData, 
          });
        } finally {
          setIsLoading(false);
        }
      }
   
    },
    [formData, onSubmit],
  );
  
  const handleSetBankAccount = (selectedBank: Bank | null) => {
    if (selectedBank) {
      setFormData((prevData) => ({
        ...prevData,
        bankName: selectedBank.label,
      }));
      setSelectedBank(selectedBank);
    setValidationResults((prevErrors) => ({
      ...prevErrors,
      bankName: '',
    }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
         bankName: '', 
      }));
      setSelectedBank(null);
    }
    clearFieldError('selectedBank', validationResults, setValidationResults);
    
  };

  

  const selected = selectedBank
  ? { value: selectedBank.value, label: selectedBank.label }
  : null;

  const handletBankAccountSearch = (searchText: string) => {
  const filteredBanks = bankAccount.filter((bank) =>
      bank.label.toLowerCase().includes(searchText.toLowerCase())
    );
    setBankAccount(filteredBanks);
   
    clearFieldError(searchText, validationResults, setValidationResults)
  };
    

  return (
    <form onSubmit={handleSubmit}>
      <fieldset>
        <div>
          <div className="flex flex-col items-center lg:items-start justify-center lg:flex lg:flex-col lg:w-[100%] lg:justify-start lg:gap-[5px] lg:ml-[4em]">
            <div className="w-[310px] sm:w-[434px] lg:w-[350px] lg:mt-[21px] lg:mb-6 mt-[35px] sm:mt-[24px] xl:mt-[] mb-[16px] sm:mb-[16px]">
              <label htmlFor="Bank Name" className="lg:block lg:font-sm lg:text-[13px] lg:text-black ">
                Bank Name
              </label>
              <SearchInput 
              data={bankAccount} 
              placeholder="Search Bank Account"
              displayKey="label"
              isLoading={isSearching}
              value={formData.bankName}
              key={initialData.bankName}
              clearIconClass='xl:mr-[2em] mr-[0.5em] lg:mr-[3em]'
              dropdownClassName="xl:w-[350px] w-[300px] md:w-[430px] "
               dropdownStyle={{
                        maxHeight: '800px',
                        overflowY: 'auto',
                      }}
                      emptyMessage={'This bank is not found'}
                      classNames="w-[310px] sm:w-[434px] border border-gray-300 bg-white max-h-[48px] min-h-[48px] placeholder:text-[15.25px] h-[39.77px] rounded p-2 mt-[5px] focus:outline-none focus:shadow-outline appearance-none lg:focus:ring-0 leading-normal xl:w-[350px lg:w-[350px]"
                      selected={selected}
                      onSelect={handleSetBankAccount}
                      handleChangeText={handletBankAccountSearch }
              />
              {validationResults.bankName && <div className="text-red-500">{validationResults.bankName}</div>}
            </div>
            <div className="w-[310px] sm:w-[434px] lg:w-[434px] lg:h-[48px] mb-[16px] sm:mb-[16px] lg:mb-6">
              <label htmlFor="account number" className="lg:block lg:font-sm lg:text-[13px] lg:text-black ">
                Account Number
              </label>
              <InputComponent
                placeholder="Account Number"
                name="accountNumber"
                type="number"
                value={formData.accountNumber}
                handleChange={handleInputChange}
                className="w-[310px] sm:w-[434px] lg:w-[350px] xl:w-[350px]"
              />
              {validationResults.accountNumber && <div className="text-red-500">{validationResults.accountNumber}</div>}
            </div>
            <div className="w-[310px] sm:w-[434px] lg:w-[434px] lg:h-[48px] lg:mt-[32px] mb-[32px]">
              <label htmlFor="account name" className="lg:block lg:font-sm lg:text-[13px] lg:text-black ">
                Account Name
              </label>
              <InputComponent
                placeholder="Account Name"
                name="accountName"
                type="text"
                value={formData.accountName}
                handleChange={handleInputChange}
                className="w-[310px] sm:w-[434px] lg:w-[350px] xl:w-[350px]"
              />
              {validationResults.accountName && <div className="text-red-500">{validationResults.accountName}</div>}
            </div>
          </div>

          <div className="lg:w-full flex justify-center sm:w-full sm:flex sm:items-center sm:justify-center lg:h-[48px] lg:flex lg:mt-[69px] lg:justify-start lg:ml-[4em]">
            <div className="w-[310px] sm:w-[434px] lg:w-[350px] xl:w-[350px] ">
              <Button type="submit" text={buttonText} isLoading={isLoading} disabled={isLoading}>
                {isLoading ? 'Loading...' : buttonText}
              </Button>
            </div>
          </div>
        </div>
      </fieldset>
    </form>
  );
};

export default BankAccountForm;
